// ONE answer to "is this a device we should spend less on?".
//
// The organism used to decide this from the user-agent alone
// (/Mobi|Android|iPhone|iPad/), which misses every case where the screen is small
// but the UA isn't a phone's: an iPad reporting as a Mac, a phone in desktop-site
// mode, a narrow window on a weak laptop — and, less academically, every headless
// or emulated phone we measure with, which is why the mobile budget looked like it
// was working when it had never once engaged in a test.
//
// A device is treated as low-power when ANY of these is true, because each one on
// its own is enough to make a full-rate, full-DPR shader the wrong call:
//   · the primary pointer is coarse (a touch screen)
//   · the viewport is phone-sized
//   · the machine admits to ≤4 logical cores
//   · the UA says phone/tablet (kept — it's still the most direct signal there is)
//
// SSR-safe: returns false when there's no window, so the first paint is never
// gated on a guess the client would then disagree with.
/** ── THE GPU TIER ────────────────────────────────────────────────────────────
 *  The tests above all describe the DEVICE CLASS, and they miss the machine that
 *  actually struggles most with a full-screen shader: a desktop with plenty of
 *  cores, a fine pointer and a large window — driving INTEGRATED graphics (or a
 *  software rasteriser). That box passes every check above and then renders our
 *  fill-rate-bound bloom at 20fps.
 *
 *  So we ask the driver. `WEBGL_debug_renderer_info` names the renderer, and the
 *  names that matter are well-known: Intel HD/UHD/Iris, AMD Vega/Radeon Graphics
 *  (the APU parts), Mali/Adreno/PowerVR (mobile parts in desktop chassis), and
 *  the software fallbacks (SwiftShader, llvmpipe, ANGLE's software backend).
 *
 *  Probed ONCE on a throwaway 1×1 context and cached — the context is released
 *  immediately, so this costs nothing after the first call. Unknown or blocked
 *  (privacy.resistFingerprinting hides the extension) reads as "fine": we never
 *  degrade a machine we can't measure.
 */
let gpuModest: boolean | null = null;
let gpuSoftware: boolean | null = null;

/** the renderer's name, read once off a throwaway context; "" when hidden */
function probeRenderer(): string {
  try {
    const c = document.createElement("canvas");
    const gl = (c.getContext("webgl2") ?? c.getContext("webgl")) as WebGLRenderingContext | null;
    if (!gl) return "";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const name = ext
      ? String(gl.getParameter((ext as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL) ?? "")
      : "";
    // release the probe context at once rather than waiting for GC
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return name;
  } catch {
    /* blocked or unavailable — treat the machine as capable */
    return "";
  }
}

const SOFTWARE = /swiftshader|llvmpipe|softwarerasterizer|basic render|microsoft basic|software/i;

/**
 * ⚠️ NO GPU AT ALL. A browser whose hardware acceleration is off (a crashed GPU
 * process, a flag, a remote desktop, a headless run) still offers WebGL — on a
 * CPU rasteriser. Measured on production: the same /company frame that a real
 * GPU draws in 8ms takes 400–900ms on SwiftShader, every frame, forever. That
 * is not "a bit slow": the main thread never comes back, navigation stalls with
 * the loading frame on screen, and the tab reads as hung. `modest` was already
 * catching this name and treating it like integrated Intel — three-quarter
 * resolution at 40fps, which on a CPU is still half a second a frame.
 */
export function isSoftwareGpu(): boolean {
  if (typeof window === "undefined") return false;
  if (gpuSoftware !== null) return gpuSoftware;
  gpuSoftware = SOFTWARE.test(probeRenderer());
  return gpuSoftware;
}

export function isModestGpu(): boolean {
  if (typeof window === "undefined") return false;
  if (gpuModest !== null) return gpuModest;
  const name = probeRenderer();
  gpuModest =
    !!name &&
    (SOFTWARE.test(name) ||
      /intel.*(hd|uhd|iris)|\bintel\b(?!.*arc)/i.test(name) ||
      /mali|adreno|powervr|videocore/i.test(name) ||
      /radeon.*(graphics|vega [3-8])\b/i.test(name));
  return gpuModest;
}

/** The spend budget, as one word. `modest` = a capable device class on weak
 *  graphics: keep full colour and softness, but fewer pixels and fewer frames. */
export function organismBudget(): "software" | "low" | "modest" | "full" {
  if (isSoftwareGpu()) return "software";
  if (isLowPowerDevice()) return "low";
  if (isModestGpu()) return "modest";
  return "full";
}

export function isLowPowerDevice(): boolean {
  if (typeof window === "undefined") return false;
  const ua = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  const small = window.innerWidth < 900;
  const fewCores = (navigator.hardwareConcurrency ?? 8) <= 4;
  return ua || coarse || small || fewCores;
}

/** True only for a real touch-screen phone/tablet form factor. */
export function isTouchScreenDevice(): boolean {
  if (typeof window === "undefined") return false;
  return (
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    ((window.matchMedia?.("(pointer: coarse)").matches ?? false) && window.innerWidth < 1200)
  );
}
